import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import { Button } from 'rebass';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1>{`Custom social card image services`}</h1>
    <p>{`Open Graph images are the image you sometimes see when sharing a link on iMessage, Slack, Twitter, etc. Though they can be any normal image, I first saw the idea of dynamically generating Open Graph images `}<a parentName="p" {...{
        "href": "https://levels.io/phantomjs-social-media-share-pictures/"
      }}>{`from @levelsio/NomadList`}</a>{` a few years ago. `}</p>
    <p>{`Earlier this year, the indomitable ZEIT team `}<a parentName="p" {...{
        "href": "https://zeit.co/blog/social-og-image-cards-as-a-service"
      }}>{`built a web service`}</a>{` for their own site’s social cards called `}<a parentName="p" {...{
        "href": "https://github.com/zeit/og-image"
      }}>{`og-image`}</a>{`, which I forked for `}<a parentName="p" {...{
        "href": "https://hackclub-workshopcards.now.sh"
      }}>{`Hack Club’s Workshop Cards`}</a>{`. Since then, they’ve rewritten the system to be simpler to run & deploy, so I used their latest version for my new projects.`}</p>
    <p>{`I’ve now created custom cards for all pages on both Notebook & my `}<a parentName="p" {...{
        "href": "https://ima.lachlanjc.me"
      }}>{`IMA blog`}</a>{`:`}</p>
    <p><img parentName="p" {...{
        "src": "https://d2wkqk610zk1ag.cloudfront.net/items/453j0U0N0p3h0I2x2s3j/IMG_0991.PNG",
        "alt": " Examples of my new social cards, composited with Photoshop for iPad"
      }}></img></p>
    <p>{`If you want to play with the systems, try them out here:`}</p>
    <Button as="a" href="https://notebook-cards.lachlanjc.now.sh" sx={{
      mr: 2
    }} mdxType="Button">Notebook demo</Button>
    <Button as="a" href="https://ima-cards.lachlanjc.now.sh" mdxType="Button">IMA demo</Button>
    <p>{`Under the hood, a Node microservice (hosted on `}<a parentName="p" {...{
        "href": "https://zeit.co/"
      }}>{`ZEIT Now`}</a>{`, of course) spins up a Puppeteer instance to render a webpage to an image file. The backend is written in TypeScript and the frontend uses `}<a parentName="p" {...{
        "href": "https://github.com/wavesoft/dot-dom"
      }}>{`.dom`}</a>{`. If you want to read more, `}<a parentName="p" {...{
        "href": "https://zeit.co/blog/social-og-image-cards-as-a-service#how-it-works"
      }}>{`ZEIT has you covered`}</a>{`.`}</p>
    <p>{`My changes:`}</p>
    <ul>
      <li parentName="ul">{`I added a `}<inlineCode parentName="li">{`caption`}</inlineCode>{` field, for displaying the course name and/or date`}</li>
      <li parentName="ul">{`I removed the default images (ZEIT logos)`}</li>
      <li parentName="ul">{`I edited the frontend, removing their image presets and custom width/height functionality and updating the style/typography`}</li>
      <li parentName="ul">{`I of course remade the final card template (primarily static HTML/CSS)`}</li>
    </ul>
    <p>{`View the final source:`}</p>
    <Button as="a" href="https://github.com/lachlanjc/notebook-cards" sx={{
      mr: 2
    }} mdxType="Button">Notebook source</Button>
    <Button as="a" href="https://github.com/lachlanjc/ima-cards" mdxType="Button">IMA source</Button>
    <p>{`If you’re looking to create your own, similar service for your own website/blog, I recommend forking `}<a parentName="p" {...{
        "href": "https://github.com/lachlanjc/notebook-cards"
      }}><inlineCode parentName="a">{`notebook-cards`}</inlineCode></a>{`. `}<inlineCode parentName="p">{`npm i`}</inlineCode>{`, `}<inlineCode parentName="p">{`now dev`}</inlineCode>{`, then jump into the `}<a parentName="p" {...{
        "href": "https://github.com/lachlanjc/notebook-cards/blob/master/api/_lib/template.ts"
      }}><inlineCode parentName="a">{`api/_lib/template.ts`}</inlineCode></a>{` file to edit the template with your own content/styling. When it’s done, just deploy via `}<inlineCode parentName="p">{`now`}</inlineCode>{` & you’re ready to go!`}</p>
    <p>{`You’ll then need to add the meta tags to links to your card service. This depends significantly on your site’s tech stack, but if you’re using React, I highly recommend making `}<a parentName="p" {...{
        "href": "https://github.com/lachlanjc/notebook/blob/345afbf4b512e5d5f4c66a15fab1d1be8ce46297/src/components/meta.js"
      }}>{`a component`}</a>{` to wrap all the necessary tags. If you’re using Next.js, you’ll need their `}<a parentName="p" {...{
        "href": "https://nextjs.org/docs#populating-head"
      }}><inlineCode parentName="a">{`Head`}</inlineCode>{` component`}</a>{`, for Gatsby, `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/docs/add-page-metadata/#using-react-helmet-and-gatsby-plugin-react-helmet"
      }}><inlineCode parentName="a">{`react-helmet`}</inlineCode>{` and its associated plugin`}</a>{`.`}</p>
    <p>{`(On Notebook, I use a completely-ridiculous method of storing post names in slugs, so `}<a parentName="p" {...{
        "href": "https://github.com/lachlanjc/notebook/commit/345afbf4b512e5d5f4c66a15fab1d1be8ce46297#diff-34a6d62af0cf0b784f8444529f3130efR42"
      }}>{`the way I generate the metadata`}</a>{` is confusing and not recommendable, but it works great for me.)`}</p>
    <p>{`One nice touch is that the service supports a dark mode, which you can automatically use for special posts. On Notebook, that’s my non-dated pages like `}<a parentName="p" {...{
        "href": "https://notebook.lachlanjc.me/shortcuts/"
      }}>{`Shortcuts`}</a>{`, and on IMA, I made midterm & final projects use dark mode. If you never touch it, that works too.`}</p>
    <p>{`Those are my systems, remixed from ZEIT’s & now open source for you to remix too. If you make your own version, let me know!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      